/**
 * Injectable record-type registry.
 *
 * `getType(name)` resolves a record-type name to its row and `listTypes()`
 * enumerates every type. Both are host-supplied — a consumer wires them with
 * `registerTypes`; until then they read `undefined` and `[]`. Pure: no client or store framework.
 */

import type { SchemaLike } from "./schemas.js"

export type RecordTypeSchema = SchemaLike

export interface RecordType {
  /** Alias of `name` (types.name is the key; the table has no id column), so a
   *  type meets the reference layer's entity contract: a resolved reference is
   *  an entity with an `id`. The catalog sets it on every type it publishes. */
  id: string
  name: string
  label: string
  plural_label: string
  position?: number
  description?: string | null
  /** The type's default schema, by id; `schema` is that schema resolved. */
  schema_id?: number | null
  schema?: RecordTypeSchema | null
}

let lister: () => RecordType[] = () => []
let resolver: (name: string) => RecordType | undefined = () => undefined

/** Wire the host's record-type catalog: `list` enumerates every type, `get` is
 *  the point lookup. Both are called on every read, never snapshotted, so a
 *  type created mid-session is visible without re-registering. */
export function registerTypes(
  list: () => RecordType[],
  get: (name: string) => RecordType | undefined,
): void {
  lister = list
  resolver = get
}

export const listTypes = (): RecordType[] => lister()
export const getType = (name: string): RecordType | undefined => resolver(name)
